<template>
    <div class="row">
        <div class="col-sm-12 mb-3" >
            <div class="d-flex flex-column gap-3 text-center align-items-center">
	            <i class="bi w-auto h-auto fs-large bi-shield-fill-minus"></i>
                <h3 class="fw-semi-bold m-0">Two Factor Authentication Setup</h3>
                <p v-if="twoFactorProgress === 'setup'" class="text-secondary m-0">Download Authenticator App and Scan the QR Code below.</p>
	            <p v-if="twoFactorProgress === 'verify'" class="text-center text-muted mt-1">Enter code from your Authenticator App to Verify.</p>
            </div>
	        <div v-if="twoFactorProgress === 'setup'" class="d-flex gap-2 align-items-center justify-content-center">
		        <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pcampaignid=web_share" target="_blank">
			        <img class="img img-fluid" :src="GoogleAuthenticator" alt="Download Google Authenticator" style="width: 4em;">
		        </a>
		        <a href="https://support.microsoft.com/en-us/account-billing/download-microsoft-authenticator-351498fc-850a-45da-b7b6-27e523b8702a" target="_blank">
			        <img class="img img-fluid" :src="MicrosoftAuthenticator" alt="Download Microsoft Authenticator" style="width: 4em;">
		        </a>
	        </div>
        </div>

        <div v-if="twoFactorProgress === 'setup'" ref="enableTwoFactorBlock" class="col-12 d-flex flex-column justify-content-center align-items-center">
	        <div class="text-center">
		        <p class="text-center text-muted mt-1">Scan the QR Code with your Authenticator App and Click the Enable Button.</p>

		        <div class="mb-4">
			        <div v-html="twoFactorSetupSvg"></div>
		        </div>

		        <p class="text-center text-muted my-1">or enter the setup key manually</p>
		        <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken my-3">
			        <input v-model="twoFactorSetupCode"  type="text" id="setup_key" name="setup_key" class="form-control m-0 border-0 fs-5 p-0 lh-fixed text-center" readonly >
			        <label for="setup_key" v-tooltip="'Copy to clipboard'">
				        <i class="me-3 dd-icon bi bi-copy" @click="helper.copyText(twoFactorSetupCode)"></i>
			        </label>
		        </div>
	        </div>
            <div class="d-flex flex-row align-items-center justify-content-center gap-3 text-center ">
                <button class="btn btn-outline-secondary rounded-pill py-2 px-3" @click="close">Cancel</button>
                <button class="btn btn-dark rounded-pill py-2 px-3" @click="twoFactorProgress = 'verify'">Enable</button>
            </div>
        </div>

	    <div v-if="twoFactorProgress === 'verify'" ref="verifyTwoFactorBlock" class="col-12">
		    <Verification :auto-submit="true" :header="false" type="two_factor" purpose="two_factor_verification" :user_id="user.id" @confirmation-status="verificationResponse" />

		    <div class="d-flex flex-row align-items-center justify-content-center gap-3 text-center mt-3">
			    <button class="btn btn-outline-secondary rounded-pill py-2 px-3" @click="close">Cancel</button>
		    </div>
	    </div>
    </div>

</template>

<script setup>
import { watch, ref, onMounted } from "vue";
import {useAppHelpers} from "@/composables/useAppHelpers.js";
import { useAuthStore } from "@/stores/auth-store.js";
import { useToast } from "@/composables/useToast.js";
import GoogleAuthenticator from "@/assets/Google_Authenticator.svg";
import MicrosoftAuthenticator from "@/assets/microsoft-authenticator-1.svg";
import Verification from "@/components/GenericModalAndViews/Verification.vue";

const emits = defineEmits(['closeEnableTwoFactor']);

const auth = useAuthStore();
const helper = useAppHelpers();
const { displayToast } = useToast();

const user = ref(auth.user);
watch(() => auth.user, (newVal) => {
	user.value = newVal;
});

const enableTwoFactorBlock = ref(null);

const twoFactorProgress = ref('setup');
const twoFAQRCodes = ref(null);
const twoFactorSetupSvg = ref(null);
const twoFactorSetupCode = ref(null);

onMounted(async () => {
	await enableTwoFactor();
});
const enableTwoFactor = async () => {
	try {
		helper.loaderOn(enableTwoFactorBlock.value);
		const response = await auth.enableTwoFactor();
		if(response.status === 200) {
			let qrResponse = await auth.twoFactorQrCode();
			if(qrResponse.response.status === 200) {
				twoFAQRCodes.value = qrResponse.data;
				twoFactorSetupSvg.value = qrResponse.svg;
				twoFactorSetupCode.value = qrResponse.code;
			}
		}
		helper.loaderOff(enableTwoFactorBlock.value);
	} catch (error) {
		helper.loaderOff(enableTwoFactorBlock.value);
	}
}

const disableTwoFactor = async () => {
	try{
		helper.loaderOn(enableTwoFactorBlock.value);
		const response = await auth.disableTwoFactor();
		helper.loaderOff(enableTwoFactorBlock.value);
	}
	catch (error) {
		helper.loaderOff(enableTwoFactorBlock.value);
	}
}

const verificationResponse = async (response) => {
	if (response[0]) {
		helper.loaderOn(enableTwoFactorBlock.value);
		displayToast('Two Factor Authentication Enabled Successfully', 'outline-success', 8000);
		await auth.FETCH_USER_DATA(true);
		emits('closeEnableTwoFactor');
		helper.loaderOff(enableTwoFactorBlock.value);
	}
}

const close = async () => {
	await disableTwoFactor();
	emits('closeEnableTwoFactor');
}

defineExpose({
    close
});
</script>
